import * as yup from 'yup';

// Email validation
export const emailSchema = yup
  .string()
  .email('Invalid email address')
  .required('Email is required');

// Password validation
export const passwordSchema = yup
  .string()
  .min(8, 'Password must be at least 8 characters')
  .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
  .matches(/[0-9]/, 'Password must contain at least one number')
  .required('Password is required');

// Phone number validation (basic) - for required phone fields
export const phoneSchema = yup
  .string()
  .matches(/^[\d\s\-\(\)\+]*$/, 'Invalid phone number format')
  .min(10, 'Phone number must be at least 10 digits');

// Optional phone number validation
export const optionalPhoneSchema = yup
  .string()
  .nullable()
  .transform((value) => value || null)
  .matches(/^[\d\s\-\(\)\+]*$/, 'Invalid phone number format');

// URL validation
export const urlSchema = yup
  .string()
  .url('Invalid URL format')
  .nullable();

// Login Form Schema
export const loginSchema = yup.object({
  email: emailSchema,
  password: passwordSchema,
});

// Volunteer Registration Schema
export const volunteerRegistrationSchema = yup.object({
  displayName: yup.string().required('Full name is required'),
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
  phoneNumber: optionalPhoneSchema,
  organizationId: yup.string().required('Please select an organization'),
  skills: yup.array().of(yup.string()).optional(),
});

// Org Admin Registration Schema (creating new organization)
export const orgAdminRegistrationSchema = yup.object({
  displayName: yup.string().required('Full name is required'),
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
  phoneNumber: optionalPhoneSchema,

  // Organization details
  organizationName: yup.string().required('Organization name is required'),
  organizationDescription: yup.string().optional(),
  website: urlSchema.optional(),
  contactEmail: yup
    .string()
    .nullable()
    .transform((value) => value || null)
    .email('Invalid email address'),
  contactPhone: optionalPhoneSchema,

  // Address
  addressStreet: yup.string().optional(),
  addressCity: yup.string().optional(),
  addressState: yup.string().optional(),
  addressZipCode: yup.string().optional(),
});

// Organization Creation Schema (standalone)
export const organizationSchema = yup.object({
  organizationName: yup
    .string()
    .required('Organization name is required')
    .min(3, 'Organization name must be at least 3 characters'),
  organizationDescription: yup.string().optional(),
  website: urlSchema.optional(),
  contactEmail: yup
    .string()
    .nullable()
    .transform((value) => value || null)
    .email('Invalid email address'),
  contactPhone: optionalPhoneSchema,
  addressStreet: yup.string().optional(),
  addressCity: yup.string().optional(),
  addressState: yup.string().optional(),
  addressZipCode: yup
    .string()
    .nullable()
    .transform((value) => value || null)
    .matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code format'),
});

// Profile Update Schema
export const profileUpdateSchema = yup.object({
  displayName: yup.string().required('Full name is required'),
  phoneNumber: optionalPhoneSchema,
  skills: yup.array().of(yup.string()).optional(),
});

// Assignment Schema (for event assignments)
export const assignmentSchema = yup.object({
  name: yup
    .string()
    .required('Assignment name is required')
    .min(2, 'Name must be at least 2 characters'),
  description: yup
    .string()
    .optional(),
  volunteersNeeded: yup
    .number()
    .required('Number of volunteers is required')
    .min(1, 'At least 1 volunteer is needed')
    .integer('Must be a whole number'),
});

// Event Edit Schema — same as createEventSchema but allows past dates (editing existing events)
export const editEventSchema = yup.object({
  title: yup
    .string()
    .required('Event name is required')
    .min(3, 'Name must be at least 3 characters'),
  description: yup
    .string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters'),
  location: yup
    .string()
    .required('Location is required'),
  date: yup
    .date()
    .required('Event date is required'),
  startTime: yup
    .string()
    .required('Start time is required')
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  endTime: yup
    .string()
    .required('End time is required')
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  volunteersNeeded: yup
    .number()
    .required('Total number of volunteers is required')
    .min(1, 'At least 1 volunteer is needed')
    .integer('Must be a whole number'),
  assignments: yup
    .array()
    .of(assignmentSchema)
    .min(1, 'At least one assignment is required')
    .required('Assignments are required'),
  isRecurring: yup
    .boolean()
    .default(false),
  recurrencePattern: yup
    .string()
    .when('isRecurring', {
      is: true,
      then: (schema) => schema.required('Recurrence pattern is required').oneOf(['weekly', 'biweekly', 'monthly', 'monthly-weekday'], 'Invalid recurrence pattern'),
      otherwise: (schema) => schema.optional(),
    }),
  monthlyWeekNumber: yup
    .string()
    .when('recurrencePattern', {
      is: 'monthly-weekday',
      then: (schema) => schema.required('Week selection is required').oneOf(['first', 'second', 'third', 'fourth', 'last'], 'Invalid week selection'),
      otherwise: (schema) => schema.optional(),
    }),
  monthlyDayOfWeek: yup
    .string()
    .when('recurrencePattern', {
      is: 'monthly-weekday',
      then: (schema) => schema.required('Day of week is required').oneOf(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'], 'Invalid day of week'),
      otherwise: (schema) => schema.optional(),
    }),
  recurrenceEndDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    })
    .when('isRecurring', {
      is: true,
      then: (schema) => schema
        .required('Recurrence end date is required')
        .min(yup.ref('date'), 'End date must be after start date')
        .test('max-one-year', 'Recurring events cannot exceed 1 year', function(value) {
          const startDate = this.parent.date;
          if (!startDate || !value) return true;
          const oneYearLater = new Date(startDate);
          oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
          return value <= oneYearLater;
        }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});

// Event Creation Schema (Basic)
export const createEventSchema = yup.object({
  title: yup
    .string()
    .required('Event name is required')
    .min(3, 'Name must be at least 3 characters'),
  description: yup
    .string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters'),
  location: yup
    .string()
    .required('Location is required'),
  date: yup
    .date()
    .required('Event date is required')
    .min(new Date(), 'Event date must be in the future'),
  startTime: yup
    .string()
    .required('Start time is required')
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  endTime: yup
    .string()
    .required('End time is required')
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  volunteersNeeded: yup
    .number()
    .required('Total number of volunteers is required')
    .min(1, 'At least 1 volunteer is needed')
    .integer('Must be a whole number'),
  assignments: yup
    .array()
    .of(assignmentSchema)
    .min(1, 'At least one assignment is required')
    .required('Assignments are required'),
  isRecurring: yup
    .boolean()
    .default(false),
  recurrencePattern: yup
    .string()
    .when('isRecurring', {
      is: true,
      then: (schema) => schema.required('Recurrence pattern is required').oneOf(['weekly', 'biweekly', 'monthly', 'monthly-weekday'], 'Invalid recurrence pattern'),
      otherwise: (schema) => schema.optional(),
    }),
  monthlyWeekNumber: yup
    .string()
    .when('recurrencePattern', {
      is: 'monthly-weekday',
      then: (schema) => schema.required('Week selection is required').oneOf(['first', 'second', 'third', 'fourth', 'last'], 'Invalid week selection'),
      otherwise: (schema) => schema.optional(),
    }),
  monthlyDayOfWeek: yup
    .string()
    .when('recurrencePattern', {
      is: 'monthly-weekday',
      then: (schema) => schema.required('Day of week is required').oneOf(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'], 'Invalid day of week'),
      otherwise: (schema) => schema.optional(),
    }),
  recurrenceEndDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      // Transform empty string to null
      return originalValue === '' ? null : value;
    })
    .when('isRecurring', {
      is: true,
      then: (schema) => schema
        .required('Recurrence end date is required')
        .min(yup.ref('date'), 'End date must be after start date')
        .test('max-one-year', 'Recurring events cannot exceed 1 year', function(value) {
          const startDate = this.parent.date;
          if (!startDate || !value) return true;

          const oneYearLater = new Date(startDate);
          oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

          return value <= oneYearLater;
        }),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});

// Event Creation Schema (Full - for future use)
export const eventSchema = yup.object({
  title: yup
    .string()
    .required('Event title is required')
    .min(3, 'Title must be at least 3 characters'),
  description: yup.string().required('Event description is required'),
  category: yup.string().required('Please select a category'),

  // Location
  locationName: yup.string().required('Location name is required'),
  locationAddress: yup.string().required('Address is required'),

  // Date and time
  startDateTime: yup
    .date()
    .required('Start date and time is required')
    .min(new Date(), 'Start date must be in the future'),
  endDateTime: yup
    .date()
    .required('End date and time is required')
    .min(yup.ref('startDateTime'), 'End date must be after start date'),

  // Requirements
  minAge: yup.number().min(0, 'Minimum age must be positive').optional(),
  requiredSkills: yup.array().of(yup.string()).optional(),
  equipment: yup.array().of(yup.string()).optional(),
});

// Volunteer Slot Schema
export const volunteerSlotSchema = yup.object({
  label: yup.string().required('Slot label is required'),
  description: yup.string().optional(),
  startTime: yup.date().required('Start time is required'),
  endTime: yup
    .date()
    .required('End time is required')
    .min(yup.ref('startTime'), 'End time must be after start time'),
  requiredSkills: yup.array().of(yup.string()).optional(),
});

// Password Reset Schema
export const passwordResetSchema = yup.object({
  email: emailSchema,
});

// Change Password Schema
export const changePasswordSchema = yup.object({
  currentPassword: passwordSchema,
  newPassword: passwordSchema
    .notOneOf([yup.ref('currentPassword')], 'New password must be different'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    .required('Please confirm your new password'),
});

// Helper function to validate field
export const validateField = async (schema, fieldName, value) => {
  try {
    await schema.validateAt(fieldName, { [fieldName]: value });
    return null; // No error
  } catch (error) {
    return error.message;
  }
};

// Helper function to validate entire form
export const validateForm = async (schema, values) => {
  try {
    await schema.validate(values, { abortEarly: false });
    return {}; // No errors
  } catch (error) {
    const errors = {};
    error.inner.forEach((err) => {
      if (err.path) {
        errors[err.path] = err.message;
      }
    });
    return errors;
  }
};

export default {
  emailSchema,
  passwordSchema,
  phoneSchema,
  optionalPhoneSchema,
  urlSchema,
  loginSchema,
  volunteerRegistrationSchema,
  orgAdminRegistrationSchema,
  organizationSchema,
  profileUpdateSchema,
  createEventSchema,
  editEventSchema,
  eventSchema,
  volunteerSlotSchema,
  passwordResetSchema,
  changePasswordSchema,
  validateField,
  validateForm,
};
